import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { BriefcaseBusiness, Clock, Coffee, Layers, MapPin, Tag, TrendingUp } from "lucide-react";
import { useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";

const typeConfig = {
    regular: {
        border: "tw-border-primary/40",
        bg: "tw-bg-indigo-50/60",
        iconClass: "tw-text-primary",
        label: null as string | null,
        labelClass: "",
        TypeIcon: null as any,
    },
    break: {
        border: "tw-border-amber-400",
        bg: "tw-bg-amber-50",
        iconClass: "tw-text-amber-600",
        label: "break" as string,
        labelClass: "tw-text-amber-700 tw-font-semibold tw-text-xs",
        TypeIcon: Coffee,
    },
    overtime: {
        border: "tw-border-purple-400",
        bg: "tw-bg-purple-50",
        iconClass: "tw-text-purple-600",
        label: "overtime" as string,
        labelClass: "tw-text-purple-700 tw-font-semibold tw-text-xs",
        TypeIcon: TrendingUp,
    },
};

function Section({ startDate, section }: { section: any; startDate: string }) {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const start = moment(startDate).add(section.start, "seconds");
    const end = moment(startDate).add(section.end, "seconds");

    const config = typeConfig[section.type as keyof typeof typeConfig] ?? typeConfig.regular;
    const { border, bg, iconClass, label, labelClass, TypeIcon } = config;

    return (
        <div className={`tw-border-l-2 ${border} ${bg} tw-rounded-r-lg tw-px-3 tw-py-2.5 tw-space-y-1.5`}>
            {TypeIcon && label && (
                <div className="tw-flex tw-items-center tw-gap-2">
                    <TypeIcon size={14} className={iconClass} />
                    <span className={labelClass}>{t(label)}</span>
                </div>
            )}
            {section.start != null && section.end != null && (
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Clock size={14} className={`${iconClass} tw-shrink-0`} />
                    <span className="tw-text-xs tw-text-neutral-600">
                        {moment.parseZone(start).format(timeFormat)}
                        {" – "}
                        {moment.parseZone(end).format(timeFormat)}
                    </span>
                </div>
            )}
            {section.job && (
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <BriefcaseBusiness size={14} className={`${iconClass} tw-shrink-0`} />
                        <span className="tw-text-xs tw-text-neutral-500">{t("job")}</span>
                    </div>
                    <span className="tw-bg-primary/20 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-text-primary tw-font-semibold">
                        {renderSettingsResourceLabel(section.job)}
                    </span>
                </div>
            )}
            {section.location && (
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <MapPin size={14} className={`${iconClass} tw-shrink-0`} />
                        <span className="tw-text-xs tw-text-neutral-500">{t("location")}</span>
                    </div>
                    <span className="tw-bg-purple-100 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-text-purple-900 tw-font-semibold">
                        {renderSettingsResourceLabel(section.location)}
                    </span>
                </div>
            )}
            {section.payCode && (
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Tag size={14} className={`${iconClass} tw-shrink-0`} />
                        <span className="tw-text-xs tw-text-neutral-500">{t("pay-code")}</span>
                    </div>
                    <span className="tw-bg-neutral-100 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-text-neutral-700 tw-font-semibold">
                        {renderSettingsResourceLabel(section.payCode)}
                    </span>
                </div>
            )}
            {(["level1", "level2", "level3", "level4"] as const).map((key, idx) => {
                const level = section[key];
                if (!level) return null;
                return (
                    <div key={key} className="tw-flex tw-items-center tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Layers size={14} className={`${iconClass} tw-shrink-0`} />
                            <span className="tw-text-xs tw-text-neutral-500">{t(`level-${idx + 1}`)}</span>
                        </div>
                        <span className="tw-bg-muted tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-text-muted-foreground tw-font-semibold">
                            {renderSettingsResourceLabel(level)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

export default Section;
